import React, { useRef, useState } from 'react';

import moment from 'moment';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { EmployeesSchedules, selectedDate, Shift } from 'types/interfaces';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import AssignmentsAbsenceCard from './AssignmentsAbsenceCard';
import PlanningParamsCard from './PlanningParamsCard';
import ShiftDialog from './ShiftDialog';
import UnassignedTasksDialog, {
  UnassignedTasksDialogRef
} from './UnassignedTasksDialog';
import { ValidationErrorsDialog } from './ValidationErrorsDialog';

const responsiveDialogContentStyle: React.CSSProperties = {
  maxHeight: 'calc(100vh - 8rem)',
  overflow: 'auto'
};

const responsiveDialogStyle = (
  width: string,
  extra?: React.CSSProperties
): React.CSSProperties => ({
  width: `min(${width}, calc(100vw - 1rem))`,
  maxWidth: 'calc(100vw - 1rem)',
  maxHeight: 'calc(100vh - 1rem)',
  ...extra
});

export interface CalendarAssignmentsDialogsProps {
  shiftDialogVisible: boolean;
  onHideScheduleCard: () => void;
  selectedDepartmentKey: string;
  shiftData: any;
  selectedDateFilter: Date | null;
  onSaveShift: (shift: any) => void;
  handleDeleteShift: (guid: string) => void;
  setShiftDialogVisible: (v: boolean) => void;
  calendarCardRef: any;

  absenceDialogVisible: boolean;
  onHideAbsenceCard: () => void;
  selectedId: number;
  absenceData: any;
  onSaveAbsence: (absence: any) => void;
  handleDeleteAbsence: (absence: any) => void;
  setAbsenceDialogVisible: (v: boolean) => void;
  absenceCardRef: any;
  departmentKey: string;
  employeesShifts?: EmployeesSchedules | null;
  newShifts?: Shift[];
  updatedShifts?: Shift[];

  selectedTaskInfo: any;
  setSelectedTaskInfo: (v: any) => void;
  onTaskClick: (id: number) => void;

  choiceDialogVisible: boolean;
  setChoiceDialogVisible: (v: boolean) => void;
  hasTaskRole: boolean;
  selectedPersonId: number | null;
  selectedDate: moment.Moment | null;
  openNewDialog: (
    type: 'shift' | 'absence' | 'task',
    personId?: number,
    date?: moment.Moment
  ) => void;
  onCreateTask: (personId?: number) => void;

  calculatorDialogVisible: boolean;
  setCalculatorDialogVisible: (v: boolean) => void;
  selectedEmployeesOptions: any[];
  handleCalculatorConfirm: (...args: any[]) => void;

  confirmRecalcDialogVisible: boolean;
  setConfirmRecalcDialogVisible: (v: boolean) => void;
  recalcParams: any;
  hasUnsavedChanges: boolean;
  handleRecalculate: () => void;

  deletionDialogVisible: boolean;
  setDeletionDialogVisible: (v: boolean) => void;
  handleMassDelete: (...args: any[]) => void;

  confirmDeletionDialogVisible: boolean;
  setConfirmDeletionDialogVisible: (v: boolean) => void;
  deletionParams: any;
  confirmMassDelete: () => void;

  deletionAbsenceDialogVisible: boolean;
  setDeletionAbsenceDialogVisible: (v: boolean) => void;
  handleMassDeleteAbsences: (...args: any[]) => void;

  confirmAbsenceDeletionDialogVisible: boolean;
  setConfirmAbsenceDeletionDialogVisible: (v: boolean) => void;
  deletionAbsenceParams: any;
  confirmMassDeleteAbsences: () => void;

  validationDialogVisible: boolean;
  validationErrors: any[];
  onHideValidation: () => void;

  unassignedTasksDialogVisible: boolean;
  setUnassignedTasksDialogVisible: (v: boolean) => void;
  unassignedTasksDepartmentId: number | null;
  unassignedTasksDateFilter: selectedDate;
  toastRef: React.RefObject<Toast | null>;
  onAfterUnassignedAssign?: () => void | Promise<void>;
}

export function CalendarAssignmentsDialogs(
  props: CalendarAssignmentsDialogsProps
) {
  const {
    shiftDialogVisible,
    onHideScheduleCard,
    selectedDepartmentKey,
    shiftData,
    selectedDateFilter,
    onSaveShift,
    handleDeleteShift,
    setShiftDialogVisible,
    calendarCardRef,

    absenceDialogVisible,
    onHideAbsenceCard,
    selectedId,
    absenceData,
    onSaveAbsence,
    handleDeleteAbsence,
    setAbsenceDialogVisible,
    absenceCardRef,
    departmentKey,
    employeesShifts,
    newShifts,
    updatedShifts,

    selectedTaskInfo,
    setSelectedTaskInfo,
    onTaskClick,

    choiceDialogVisible,
    setChoiceDialogVisible,
    hasTaskRole,
    selectedPersonId,
    selectedDate,
    openNewDialog,
    onCreateTask,

    calculatorDialogVisible,
    setCalculatorDialogVisible,
    selectedEmployeesOptions,
    handleCalculatorConfirm,

    confirmRecalcDialogVisible,
    setConfirmRecalcDialogVisible,
    recalcParams,
    hasUnsavedChanges,
    handleRecalculate,

    deletionDialogVisible,
    setDeletionDialogVisible,
    handleMassDelete,

    confirmDeletionDialogVisible,
    setConfirmDeletionDialogVisible,
    deletionParams,
    confirmMassDelete,

    deletionAbsenceDialogVisible,
    setDeletionAbsenceDialogVisible,
    handleMassDeleteAbsences,

    confirmAbsenceDeletionDialogVisible,
    setConfirmAbsenceDeletionDialogVisible,
    deletionAbsenceParams,
    confirmMassDeleteAbsences,

    validationDialogVisible,
    validationErrors,
    onHideValidation,

    unassignedTasksDialogVisible,
    setUnassignedTasksDialogVisible,
    unassignedTasksDepartmentId,
    unassignedTasksDateFilter,
    toastRef,
    onAfterUnassignedAssign
  } = props;

  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const unassignedTasksRef = useRef<UnassignedTasksDialogRef | null>(null);

  return (
    <>
      <Dialog
        header="Настройка смены"
        visible={shiftDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--shift"
        style={responsiveDialogStyle('630px')}
        breakpoints={{ '960px': '75vw', '641px': 'calc(100vw - 1rem)' }}
        maximizable
        modal
        contentStyle={{
          ...responsiveDialogContentStyle,
          minHeight: '300px'
        }}
        onHide={onHideScheduleCard}
        draggable={!isCalendarOpen}
      >
        <VisibilityWrapper code="calendarAssignments-shiftCard__roles">
          <ShiftDialog
            departmentId={selectedDepartmentKey || ''}
            shift={shiftData}
            selectedDateFilter={selectedDateFilter}
            onSave={onSaveShift}
            onDelete={handleDeleteShift}
            onClose={() => setShiftDialogVisible(false)}
            ref={calendarCardRef}
            setIsCalendarOpen={setIsCalendarOpen}
          />
        </VisibilityWrapper>
      </Dialog>

      <Dialog
        header="Редактирование отсутствия"
        visible={absenceDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--absence"
        style={responsiveDialogStyle('630px')}
        contentStyle={responsiveDialogContentStyle}
        onHide={onHideAbsenceCard}
      >
        <VisibilityWrapper code="calendarAssignments-shiftCard__roles">
          <AssignmentsAbsenceCard
            id={selectedId}
            absence={absenceData}
            onSave={onSaveAbsence}
            onDelete={handleDeleteAbsence}
            onClose={() => setAbsenceDialogVisible(false)}
            departmentId={departmentKey || ''}
            ref={absenceCardRef}
            employeesShifts={employeesShifts}
            newShifts={newShifts}
            updatedShifts={updatedShifts}
          />
        </VisibilityWrapper>
      </Dialog>

      <Dialog
        header="Информация о нарядах"
        visible={!!selectedTaskInfo}
        onHide={() => setSelectedTaskInfo(null)}
        className="calendarAssignments-dialog calendarAssignments-dialog--task-info"
        style={responsiveDialogStyle('600px')}
        contentStyle={responsiveDialogContentStyle}
        pt={withDialogFooterSpacing()}
        footer={
          <Button
            label="Закрыть"
            onClick={() => setSelectedTaskInfo(null)}
            severity="secondary"
          />
        }
      >
        <div className="task-info-list flex flex-column gap-2">
          {selectedTaskInfo?.tasks.map((task: any) => (
            <div
              key={task.id}
              className="p-mb-3 p-p-2 surface-0 shadow-1 border-round p-2 flex flex-column gap-2"
            >
              <h4>
                {task.taskType?.label || 'Наряд'} #{task.id}
                {task.isClosed && (
                  <i
                    className="pi pi-exclamation-circle closing-status-tooltip"
                    style={{
                      fontSize: '1rem',
                      color: 'rgb(255 168 0)',
                      cursor: 'pointer',
                      marginLeft: 5
                    }}
                    data-pr-tooltip="Наряд находится в закрывающем статусе"
                  />
                )}
                <Tooltip target=".closing-status-tooltip" />
              </h4>
              <p>
                <strong>Дата регистрации:</strong>{' '}
                {moment(task.creationTime).format('DD.MM.YYYY HH:mm')}
              </p>
              {task.planStart && (
                <p>
                  <strong>Дата планового начала:</strong>{' '}
                  {moment(task.planStart).format('DD.MM.YYYY HH:mm')}
                </p>
              )}
              {task.factStart && (
                <p>
                  <strong>Дата фактического начала:</strong>{' '}
                  {moment(task.factStart).format('DD.MM.YYYY HH:mm')}
                </p>
              )}
              {task.planFinish && (
                <p>
                  <strong>Дата планового завершения:</strong>{' '}
                  {moment(task.planFinish).format('DD.MM.YYYY HH:mm')}
                </p>
              )}
              {task.factFinish && (
                <p>
                  <strong>Дата фактического завершения:</strong>{' '}
                  {moment(task.factFinish).format('DD.MM.YYYY HH:mm')}
                </p>
              )}
              <Button
                label="Перейти к наряду"
                icon="pi pi-external-link"
                onClick={() => onTaskClick(task.id)}
                className="p-mt-2"
                severity="info"
              />
            </div>
          ))}
        </div>
      </Dialog>

      <Dialog
        header="Выберите действие"
        visible={choiceDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--choice"
        style={responsiveDialogStyle(hasTaskRole ? '605px' : '400px')}
        contentStyle={responsiveDialogContentStyle}
        onHide={() => setChoiceDialogVisible(false)}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="calendarAssignments-choice-actions flex flex-wrap gap-2">
            <Button
              className="flex-1 calendarAssignments-add-btn__roles wholeui__roles"
              label="Добавить смену"
              severity="success"
              onClick={() => {
                if (selectedPersonId && selectedDate) {
                  openNewDialog('shift', selectedPersonId, selectedDate);
                }
                setChoiceDialogVisible(false);
              }}
            />
            <Button
              className="flex-1 calendarAssignments-add-btn__roles wholeui__roles"
              label="Добавить отсутствие"
              severity="secondary"
              onClick={() => {
                if (selectedPersonId && selectedDate) {
                  openNewDialog('absence', selectedPersonId, selectedDate);
                }
                setChoiceDialogVisible(false);
              }}
            />
            <Button
              label="Добавить наряд"
              severity="info"
              className="flex-1 tasks-add-btn__roles whole wholeui__roles"
              onClick={() => {
                if (selectedPersonId && selectedDate) {
                  onCreateTask(selectedPersonId);
                }
                setChoiceDialogVisible(false);
              }}
            />
          </div>
        }
      >
        <p>
          Выберите, что вы хотите добавить для сотрудника на выбранную дату.
        </p>
      </Dialog>

      <Dialog
        header="Параметры планирования"
        visible={calculatorDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--planning"
        style={responsiveDialogStyle('800px')}
        contentStyle={responsiveDialogContentStyle}
        onHide={() => setCalculatorDialogVisible(false)}
      >
        <PlanningParamsCard
          selectedDateFilter={selectedDateFilter}
          employeesOptions={selectedEmployeesOptions}
          onCancel={() => setCalculatorDialogVisible(false)}
          onConfirm={handleCalculatorConfirm}
        />
      </Dialog>
      <Dialog
        className="calendarAssignments-dialog calendarAssignments-dialog--confirm"
        style={responsiveDialogStyle('500px')}
        contentStyle={responsiveDialogContentStyle}
        header="Подтверждение пересчета графика"
        visible={confirmRecalcDialogVisible}
        onHide={() => setConfirmRecalcDialogVisible(false)}
        pt={withDialogFooterSpacing()}
        footer={
          <div>
            <Button
              className="p-button-secondary"
              label="Отмена"
              onClick={() => setConfirmRecalcDialogVisible(false)}
            />
            <Button
              className="p-button-danger"
              label="Подтвердить"
              onClick={handleRecalculate}
            />
          </div>
        }
      >
        <p>
          Пересчет графика повлечет за собой{' '}
          {recalcParams?.shouldClear ? 'полную перезапись' : 'добавление'}{' '}
          рабочих смен.
        </p>
        {recalcParams?.shouldClear && (
          <p>
            <b>Все существующие смены будут удалены.</b>
          </p>
        )}
        {recalcParams?.shouldDeleteAbsences && (
          <p>
            <b>Все существующие отсутствия будут удалены.</b>
          </p>
        )}
        {hasUnsavedChanges && (
          <p style={{ color: 'red' }}>
            Внимание: все несохраненные изменения будут потеряны.
          </p>
        )}
      </Dialog>

      <Dialog
        header="Удаление смен"
        visible={deletionDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--planning"
        style={responsiveDialogStyle('800px')}
        contentStyle={responsiveDialogContentStyle}
        onHide={() => setDeletionDialogVisible(false)}
      >
        <PlanningParamsCard
          mode="deletion"
          selectedDateFilter={selectedDateFilter}
          employeesOptions={selectedEmployeesOptions}
          onCancel={() => setDeletionDialogVisible(false)}
          onConfirm={handleMassDelete}
        />
      </Dialog>
      <Dialog
        className="calendarAssignments-dialog calendarAssignments-dialog--confirm"
        style={responsiveDialogStyle('500px')}
        contentStyle={responsiveDialogContentStyle}
        header="Подтверждение удаления смен"
        visible={confirmDeletionDialogVisible}
        onHide={() => setConfirmDeletionDialogVisible(false)}
        pt={withDialogFooterSpacing()}
        footer={
          <div>
            <Button
              className="p-button-secondary"
              label="Отмена"
              onClick={() => setConfirmDeletionDialogVisible(false)}
            />
            <Button
              className="p-button-danger"
              label="Удалить"
              onClick={confirmMassDelete}
            />
          </div>
        }
      >
        <p>
          Вы уверены, что хотите удалить смены у выбранных сотрудников за
          указанный период?
        </p>
        {deletionParams && (
          <>
            <p>
              <b>Период:</b>{' '}
              {moment(deletionParams.startDate).format('DD.MM.YYYY')} -{' '}
              {moment(deletionParams.endDate).format('DD.MM.YYYY')}
            </p>
            <p>
              <b>Количество сотрудников:</b>{' '}
              {deletionParams.selectedEmployees.length}
            </p>
          </>
        )}
      </Dialog>

      <Dialog
        header="Удаление отсутствий"
        visible={deletionAbsenceDialogVisible}
        className="calendarAssignments-dialog calendarAssignments-dialog--planning"
        style={responsiveDialogStyle('800px')}
        contentStyle={responsiveDialogContentStyle}
        onHide={() => setDeletionAbsenceDialogVisible(false)}
      >
        <PlanningParamsCard
          mode="absence_deletion"
          selectedDateFilter={selectedDateFilter}
          employeesOptions={selectedEmployeesOptions}
          onCancel={() => setDeletionAbsenceDialogVisible(false)}
          onConfirm={handleMassDeleteAbsences}
        />
      </Dialog>
      <Dialog
        className="calendarAssignments-dialog calendarAssignments-dialog--confirm"
        style={responsiveDialogStyle('500px')}
        contentStyle={responsiveDialogContentStyle}
        header="Подтверждение удаления отсутствий"
        visible={confirmAbsenceDeletionDialogVisible}
        onHide={() => setConfirmAbsenceDeletionDialogVisible(false)}
        pt={withDialogFooterSpacing()}
        footer={
          <div>
            <Button
              className="p-button-secondary"
              label="Отмена"
              onClick={() => setConfirmAbsenceDeletionDialogVisible(false)}
            />
            <Button
              className="p-button-danger"
              label="Удалить"
              onClick={confirmMassDeleteAbsences}
            />
          </div>
        }
      >
        <p>
          Вы уверены, что хотите удалить отсутствия у выбранных сотрудников за
          указанный период?
        </p>
        {deletionAbsenceParams && (
          <>
            <p>
              <b>Период:</b>{' '}
              {moment(deletionAbsenceParams.startDate).format('DD.MM.YYYY')} -{' '}
              {moment(deletionAbsenceParams.endDate).format('DD.MM.YYYY')}
            </p>
            <p>
              <b>Количество сотрудников:</b>{' '}
              {deletionAbsenceParams.selectedEmployees.length}
            </p>
          </>
        )}
      </Dialog>

      <ValidationErrorsDialog
        visible={validationDialogVisible}
        errors={validationErrors as any}
        employees={selectedEmployeesOptions}
        onHide={onHideValidation}
      />

      <Dialog
        visible={unassignedTasksDialogVisible}
        header="Неназначенные наряды"
        className="calendarAssignments-dialog calendarAssignments-dialog--unassigned"
        style={{
          width: 'calc(100vw - 1rem)',
          maxWidth: 2100,
          maxHeight: 'calc(100vh - 1rem)'
        }}
        contentStyle={responsiveDialogContentStyle}
        modal
        onHide={() => unassignedTasksRef.current?.handleCancel()}
        pt={withDialogFooterSpacing()}
      >
        <UnassignedTasksDialog
          ref={unassignedTasksRef}
          visible={unassignedTasksDialogVisible}
          departmentId={unassignedTasksDepartmentId}
          dateFilter={unassignedTasksDateFilter}
          toastRef={toastRef}
          onClose={() => setUnassignedTasksDialogVisible(false)}
          onAfterAssign={onAfterUnassignedAssign}
        />
      </Dialog>
    </>
  );
}
